<div class="p-6">

  <!-- Header -->
  <div class="flex items-center justify-between mb-5">
    <div>
      <h1 class="text-xl font-semibold text-gray-900 dark:text-white">Endpoints Webhook</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        Gestiona los endpoints inbound para disparadores <strong>INBOUND_WEBHOOK</strong>.
      </p>
    </div>
    <div class="flex gap-2">
      <button (click)="loadEndpoints()"
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors">
        Actualizar
      </button>
      <button *appHasPermission="'workflows:create'" (click)="openCreateModal()"
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        Nuevo endpoint
      </button>
    </div>
  </div>

  <!-- Security notice -->
  <div class="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-300">
    <p class="font-semibold mb-1">Seguridad de endpoints</p>
    <p>Los endpoints sin secret son inseguros para producción. El backend nunca devuelve el secret ni el hash. Si lo olvidas, genera uno nuevo.</p>
  </div>

  <!-- El éxito va por ToastService (INC_CRM_020 (2) inc11); el error queda fijo abajo. -->
  @if (error) {
    <div class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex justify-between text-sm">
      <span>{{ error }}</span>
      <button (click)="error = ''">&times;</button>
    </div>
  }

  <!-- Table -->
  <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    @if (loading) {
      <div class="flex justify-center py-12 text-gray-400">
        <svg class="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Cargando endpoints...
      </div>
    } @else if (endpoints.length === 0) {
      <div class="flex flex-col items-center justify-center py-14 text-gray-400">
        <svg class="w-10 h-10 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
        <p class="text-base font-medium text-gray-500 dark:text-gray-400">Sin endpoints</p>
        <p class="text-sm text-gray-400 mt-1">Crea el primero para recibir webhooks entrantes.</p>
      </div>
    } @else {
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Nombre</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Endpoint Key</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Workflow</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Nodo</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Activo</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Secret</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Advertencia</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Creado</th>
              <th class="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 text-xs">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            @for (ep of endpoints; track ep.id) {
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td class="px-3 py-3 font-medium text-xs">
                  <button (click)="openDetailModal(ep)" class="text-gray-900 dark:text-white hover:text-sky-600 hover:underline text-left">
                    {{ ep.name ?? '—' }}
                  </button>
                  @if (ep.description) {
                    <p class="text-[10px] text-gray-400 font-normal">{{ ep.description }}</p>
                  }
                </td>
                <td class="px-3 py-3">
                  <div class="flex items-center gap-1.5">
                    <span class="font-mono text-xs text-sky-700 dark:text-sky-400">{{ ep.endpointKey }}</span>
                    <button (click)="copyUrl(ep)" title="Copiar URL inbound"
                            class="p-0.5 text-gray-400 hover:text-sky-600 transition-colors">
                      @if (copiedId === ep.id) {
                        <svg class="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                      } @else {
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                      }
                    </button>
                  </div>
                </td>
                <td class="px-3 py-3 text-xs text-gray-700 dark:text-gray-300">
                  {{ ep.workflowName ?? ('#' + ep.idWorkflow) }}
                </td>
                <td class="px-3 py-3 text-xs text-gray-600 dark:text-gray-400">
                  {{ ep.nodeName ?? '—' }}
                </td>
                <td class="px-3 py-3">
                  <button (click)="toggleActive(ep)"
                          [appDisableWithoutPermission]="'workflows:edit'"
                          [ngClass]="ep.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'"
                          class="px-2 py-0.5 rounded-full text-[10px] font-medium">
                    {{ ep.isActive ? 'Activo' : 'Inactivo' }}
                  </button>
                </td>
                <td class="px-3 py-3">
                  @if (ep.hasSecret) {
                    <span class="inline-flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      Sí
                    </span>
                  } @else {
                    <span class="text-[10px] text-amber-600">Sin secret</span>
                  }
                </td>
                <td class="px-3 py-3">
                  @if (ep.securityWarning) {
                    <span class="text-[10px] text-red-500" [title]="ep.securityWarning">⚠ {{ ep.securityWarning | slice:0:30 }}{{ ep.securityWarning.length > 30 ? '...' : '' }}</span>
                  } @else {
                    <span class="text-gray-300 text-[10px]">—</span>
                  }
                </td>
                <td class="px-3 py-3 text-xs text-gray-500">{{ ep.datetimeCreated | date:'dd/MM/yy HH:mm' }}</td>
                <td class="px-3 py-3">
                  <div class="flex items-center gap-1">
                    <button (click)="openTestInbound(ep)" title="Probar"
                            class="p-1 text-gray-400 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3l14 9-14 9V3z"/>
                      </svg>
                    </button>
                    <button (click)="openEditModal(ep)" title="Editar"
                            [appDisableWithoutPermission]="'workflows:edit'"
                            class="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button (click)="openDeleteModal(ep)" title="Eliminar"
                            [appDisableWithoutPermission]="'workflows:delete'"
                            class="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>

<!-- Modal: Create / Edit endpoint -->
@if (showFormModal) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-4">
        {{ editingId === null ? 'Nuevo endpoint' : 'Editar endpoint' }}
      </h3>
      @if (formError) {
        <div class="mb-3 p-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded">{{ formError }}</div>
      }
      <div class="space-y-3">
        <div class="space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Workflow *</label>
            <select [(ngModel)]="fIdWorkflow" (ngModelChange)="onWorkflowChange()"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500">
              <option [ngValue]="null" disabled>{{ workflowsLoading ? 'Cargando workflows...' : 'Selecciona un workflow' }}</option>
              @for (wf of workflows; track wf.id) {
                <option [ngValue]="wf.id">{{ wf.name }}</option>
              }
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Evento que activará el workflow *</label>
            <select [(ngModel)]="fIdNode" [disabled]="!fIdWorkflow || nodesLoading"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50">
              <option [ngValue]="null" disabled>
                {{ !fIdWorkflow ? 'Primero selecciona un workflow' : (nodesLoading ? 'Cargando eventos...' : (nodes.length === 0 ? 'Este workflow no tiene eventos configurados' : 'Selecciona un evento')) }}
              </option>
              @for (node of nodes; track node.id) {
                <option [ngValue]="node.id">{{ node.title || nodeSubtypeText(node.nodeSubtype) }}</option>
              }
            </select>
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Endpoint Key *</label>
          <input [(ngModel)]="fEndpointKey" placeholder="Ej: stripe-payment-completed"
                 name="wh-endpoint-key" autocomplete="off" data-lpignore="true"
                 class="w-full px-3 py-2 text-sm font-mono border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
          <p class="text-[10px] text-gray-400 mt-0.5">Solo letras minúsculas, números y guiones.</p>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
          <input [(ngModel)]="fName" placeholder="Ej: Stripe pagos completados"
                 name="wh-endpoint-name" autocomplete="off" data-lpignore="true"
                 class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
          <textarea [(ngModel)]="fDescription" rows="2" placeholder="Descripción opcional"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"></textarea>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Clave de seguridad
            <span class="text-gray-400 font-normal ml-1">{{ editingId !== null ? '(dejar vacío para no cambiar)' : '(recomendado)' }}</span>
          </label>
          <input type="password" [(ngModel)]="fSecret" placeholder="Escribe una clave secreta o déjalo vacío"
                 name="wh-endpoint-secret" autocomplete="new-password" data-lpignore="true"
                 class="w-full px-3 py-2 text-sm font-mono border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
          <p class="text-[10px] text-gray-400 mt-0.5">
            Esta clave sirve para verificar que los datos realmente vienen de la aplicación externa (como Stripe, un formulario, etc.), y no de alguien más. Configúrala también en la app externa que va a enviar los datos.
          </p>
          <p class="text-[10px] text-amber-600 mt-0.5">
            Por seguridad, esta clave no se vuelve a mostrar una vez guardada. Si la olvidas, genera una nueva.
          </p>
        </div>
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" [(ngModel)]="fIsActive" class="w-4 h-4 rounded text-sky-600"/>
          <span class="text-sm text-gray-700 dark:text-gray-300">Activo</span>
        </label>
      </div>
      <div class="flex gap-2 mt-5 justify-end">
        <button (click)="showFormModal = false" class="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
        <button (click)="confirmSave()" [disabled]="formLoading"
                class="px-4 py-2 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50">
          {{ formLoading ? 'Guardando...' : (editingId === null ? 'Crear' : 'Guardar') }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Modal: Delete confirmation -->
@if (showDeleteModal && deletingEndpoint) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-3">Eliminar endpoint</h3>
      @if (deleteError) {
        <div class="mb-3 p-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded">{{ deleteError }}</div>
      }
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        ¿Eliminar el endpoint <strong class="font-mono">{{ deletingEndpoint.endpointKey }}</strong>?
        Esta acción es irreversible.
      </p>
      <div class="flex gap-2 justify-end">
        <button (click)="showDeleteModal = false" class="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
        <button (click)="confirmDelete()" [disabled]="deleteLoading"
                class="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
          {{ deleteLoading ? 'Eliminando...' : 'Eliminar' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Modal: Test inbound -->


<!-- Modal: Detalle del endpoint -->
@if (showDetailModal && detailEndpoint) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[85vh] overflow-y-auto">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">
          {{ detailEndpoint.name ?? detailEndpoint.endpointKey }}
        </h3>
        <button (click)="showDetailModal = false" class="text-gray-400 hover:text-gray-600">&times;</button>
      </div>

      <div class="space-y-4 text-sm">

        @if (detailEndpoint.description) {
          <div>
            <p class="text-xs text-gray-400 mb-0.5">Descripción</p>
            <p class="text-gray-700 dark:text-gray-300">{{ detailEndpoint.description }}</p>
          </div>
        }

        <div class="grid grid-cols-2 gap-3">
          <div>
            <p class="text-xs text-gray-400 mb-0.5">Estado</p>
            <span [ngClass]="detailEndpoint.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'"
                  class="px-2 py-0.5 rounded-full text-xs font-medium">
              {{ detailEndpoint.isActive ? 'Activo' : 'Inactivo' }}
            </span>
          </div>
          <div>
            <p class="text-xs text-gray-400 mb-0.5">Clave de seguridad</p>
            @if (detailEndpoint.hasSecret) {
              <span class="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">Configurada</span>
            } @else {
              <span class="text-xs text-amber-600">No configurada</span>
            }
          </div>
        </div>

        <div>
          <p class="text-xs text-gray-400 mb-0.5">Workflow asociado</p>
          <p class="text-gray-700 dark:text-gray-300">{{ detailEndpoint.workflowName ?? '—' }}</p>
        </div>

        @if (detailEndpoint.nodeName) {
          <div>
            <p class="text-xs text-gray-400 mb-0.5">Evento que activa el workflow</p>
            <p class="text-gray-700 dark:text-gray-300">{{ detailEndpoint.nodeName }}</p>
          </div>
        }

        <div class="grid grid-cols-2 gap-3">
          <div>
            <p class="text-xs text-gray-400 mb-0.5">Creado por</p>
            <p class="text-gray-700 dark:text-gray-300">{{ detailEndpoint.createdByName ?? '—' }}</p>
            <p class="text-[10px] text-gray-400">{{ detailEndpoint.datetimeCreated | date:'dd/MM/yy HH:mm' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-400 mb-0.5">Última actualización por</p>
            <p class="text-gray-700 dark:text-gray-300">{{ detailEndpoint.updatedByName ?? '—' }}</p>
            <p class="text-[10px] text-gray-400">{{ detailEndpoint.datetimeUpdated | date:'dd/MM/yy HH:mm' }}</p>
          </div>
        </div>

        <div>
          <p class="text-xs text-gray-400 mb-1">Cómo invocar este webhook</p>
          <div class="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-2">
            <span class="font-mono text-xs text-sky-700 dark:text-sky-400 break-all">{{ getInboundUrl(detailEndpoint) }}</span>
            <button (click)="copyUrl(detailEndpoint)" title="Copiar URL"
                    class="p-1 text-gray-400 hover:text-sky-600 transition-colors shrink-0">
              @if (copiedId === detailEndpoint.id) {
                <svg class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              } @else {
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              }
            </button>
          </div>
          <p class="text-[10px] text-gray-400 mt-1">
            Copia esta URL y pégala en la aplicación externa (Stripe, formulario, etc.) para que te envíe información aquí.
          </p>
        </div>

      </div>

      <div class="flex justify-end mt-5">
        <button (click)="showDetailModal = false" class="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
          Cerrar
        </button>
      </div>
    </div>
  </div>
}